import { getAdminProductUrl } from '@/app/products/api/endpoints.ts';
import { AppDialog } from '@/components/app/app-dialog.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { d } from '@/lib/form.ts';
import { AppForm } from '@/components/form/form.tsx';
import {
  useAdminProductAvailabilityOptsQuery,
  useAdminProductColorOptsQuery,
  useAdminProductConstructionOptsQuery,
  useAdminProductPatternOptsQuery,
  useAdminProductQuery,
  useAdminProductReindexMutation,
  useAdminProductSaveMutation,
  useAdminProductVisibilityOptsQuery,
} from '@/app/products/api/queries.ts';
import { useEffect } from 'react';
import { SelectField } from '@/components/form/select-field.tsx';
import { useGlobalLoader } from '@/hooks/use-global-loader.ts';
import { MultiSelectField } from '@/components/form/multi-select-field.tsx';
import { Slider } from '@/components/ui/slider.tsx';
import { FieldLabel } from '@/components/ui/field.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ProductTypeEnum, TAdminProduct, TListProduct } from '@/types/models.ts';
import { setUiUpdated } from '@/stores/ui.store.ts';
import { TLabelValue } from '@/types/misc.ts';

function getTitle(item: TListProduct, index: number) {
  const color = item.colors[index] ? ` - ${item.colors[index].name}` : '';
  return `Edit: ${item.name}${color}`;
}

const formSchema = z.object({
  id: z.number(),
  visibility: z.string(),
  availability: z.string(),
  construction: z.array(z.string()),
  patterns: z.array(z.string()),
  rating: z.number(),
  product_type: z.coerce.number(),
  color: z.object({
    id: z.number(),
    visibility: z.string(),
    availability: z.string(),
    primary: z.array(z.string()),
    secondary: z.array(z.string()),
  }),
});
type FormType = z.infer<typeof formSchema>;

function getDefaultValues(product: TAdminProduct): FormType {
  return {
    id: d.num(product?.id),
    visibility: d.str(product?.visibility),
    availability: d.str(product?.availability),
    construction: product?.construction ? product.construction.map((c) => String(c.id)) : [],
    patterns: product?.patterns ? product.patterns.map((p) => String(p.id)) : [],
    rating: d.num(product?.rating || 0),
    product_type: d.num(product?.product_type),
    color: {
      id: d.num(product?.color?.id),
      visibility: d.str(product?.color?.visibility),
      availability: d.str(product?.color?.availability),
      primary: product?.color?.primary ? product.color.primary.map((c) => String(c.id)) : [],
      secondary: product?.color?.secondary ? product.color.secondary.map((c) => String(c.id)) : [],
    },
  };
}

function isBroadloomOrCustom(product: TAdminProduct) {
  const type = product.product_type;
  return type === ProductTypeEnum.BROADLOOM || type === ProductTypeEnum.CUSTOM;
}

const typeOpts: TLabelValue[] = [
  { label: 'Custom', value: String(ProductTypeEnum.CUSTOM) },
  { label: 'Broadloom', value: String(ProductTypeEnum.BROADLOOM) },
];

type Props = {
  item: TListProduct;
  currIndex: number;
  onClose: () => void;
};

export function EditProductDialog({ item, currIndex, onClose }: Props) {
  const color = item.colors[currIndex] || null;
  const { data: product, isLoading } = useAdminProductQuery(item.id, color?.id);
  useGlobalLoader(isLoading);
  const saveProduct = useAdminProductSaveMutation();
  const reindexProduct = useAdminProductReindexMutation();
  const { data: visibilityOpts } = useAdminProductVisibilityOptsQuery();
  const { data: availabilityOpts } = useAdminProductAvailabilityOptsQuery();
  const { data: constructionOpts } = useAdminProductConstructionOptsQuery();
  const { data: patternOpts } = useAdminProductPatternOptsQuery();
  const { data: colorOpts } = useAdminProductColorOptsQuery();
  const img = color && color.img ? color.img : item.img;
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(product!),
  });

  const onRatingSliderChange = (value: number[]) => {
    form.setValue('rating', value[0], { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = (values: FormType) => {
    saveProduct.mutateAsync(values).then(() => {
      setUiUpdated();
      onClose();
    });
  };
  const onReIndex = () => {
    reindexProduct.mutateAsync(item.id).then(() => {
      setUiUpdated();
      onClose();
    });
  };
  const rating = form.watch('rating');

  useEffect(() => {
    form.reset(getDefaultValues(product!));
  }, [product]);
  if (!product) return null;
  return (
    <AppDialog title={getTitle(item, currIndex)} onClose={onClose} size="lg">
      <AppForm form={form} onSubmit={onSubmit}>
        <div className="dialog-body">
          <div className="grid lg:grid-cols-3 gap-3">
            <div className="order-2">{img && <img className="object-cover" src={img} alt="" />}</div>
            <div className="lg:col-span-2">
              <div className="flex flex-col gap-3">
                <h3>Product Attributes For: {product?.name}</h3>
                {isBroadloomOrCustom(product) ? (
                  <SelectField name="product_type" label="Type" items={typeOpts} orientation="horizontal" />
                ) : (
                  <div className="flex items-center gap-4">
                    <FieldLabel>Type:</FieldLabel>
                    <label>{product.type_name}</label>
                  </div>
                )}
                <div className="grid lg:grid-cols-2 gap-3">
                  <SelectField
                    name="visibility"
                    label="Visibility:"
                    items={visibilityOpts || []}
                    orientation="horizontal"
                  />
                  <SelectField
                    name="availability"
                    label="Availability:"
                    items={availabilityOpts || []}
                    orientation="horizontal"
                  />
                </div>
                <MultiSelectField
                  className="control-grow"
                  label="Construction:"
                  orientation="horizontal"
                  name="construction"
                  options={constructionOpts || []}
                  triggerClassName="form-control"
                  modalPopover={true}
                />
                <MultiSelectField
                  className="control-grow"
                  label="Pattern:"
                  orientation="horizontal"
                  name="patterns"
                  options={patternOpts || []}
                  triggerClassName="form-control"
                  modalPopover={true}
                />
                <div className="flex items-center gap-4">
                  <FieldLabel>Rating:</FieldLabel>
                  <Slider defaultValue={[product.rating]} max={10} step={0.5} onValueChange={onRatingSliderChange} />
                  <label>{rating}</label>
                </div>
                <div className="flex items-center gap-4">
                  <FieldLabel>Cost:</FieldLabel>
                  <label>{product.cost_str}</label>
                </div>
                <h3 className="mt-4">Color Attributes For: {product?.color.name}</h3>
                <div className="grid lg:grid-cols-2 gap-3">
                  <SelectField
                    name="color.visibility"
                    label="Color Visibility:"
                    items={visibilityOpts || []}
                    orientation="horizontal"
                  />
                  <SelectField
                    name="color.availability"
                    label="Color Availability:"
                    items={availabilityOpts || []}
                    orientation="horizontal"
                  />
                </div>
                <MultiSelectField
                  className="control-grow"
                  label="Color Primary:"
                  orientation="horizontal"
                  name="color.primary"
                  options={colorOpts || []}
                  triggerClassName="form-control"
                  modalPopover={true}
                />
                <MultiSelectField
                  className="control-grow"
                  label="Color Secondary:"
                  orientation="horizontal"
                  name="color.secondary"
                  options={colorOpts || []}
                  triggerClassName="form-control"
                  modalPopover={true}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="dialog-footer">
          <Button disabled={!form.formState.isDirty}>Save</Button>
          <Button variant="outline" asChild>
            <a href={getAdminProductUrl(item.id)} target="_blank">
              View Admin
            </a>
          </Button>
          <Button type="button" variant="outline" onClick={onReIndex}>
            Re-Index
          </Button>
        </div>
      </AppForm>
    </AppDialog>
  );
}
